<template>
  <b-collapse class="card" :open="isOpen" @open="onOpen">
    <template #trigger="props">
      <div class="card-header" role="button">
        <p class="card-header-title">
          <b-icon :icon="props.open ? 'menu-down' : 'menu-right'"></b-icon>
          Episode Processing
          <span v-if="processingStatus" class="ml-2">
            <b-tag :type="getStatusTagType()">{{ processingStatus }}</b-tag>
          </span>
        </p>
      </div>
    </template>
    <div class="card-content">
      <!-- Mode Selection Header -->
      <div class="mode-selection-header mb-4">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <b-field label="Source Type" label-position="on-border">
                <b-radio-button
                  v-model="sourceMode"
                  native-value="single"
                  type="is-info"
                >
                  <span class="icon"><i class="mdi mdi-file-video"></i></span>
                  <span>Single TX File</span>
                </b-radio-button>
                <b-radio-button
                  v-model="sourceMode"
                  native-value="multi"
                  type="is-success"
                >
                  <span class="icon"><i class="mdi mdi-file-multiple"></i></span>
                  <span>Separate Segment Files</span>
                </b-radio-button>
              </b-field>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item" v-if="lastUpdated">
              <span class="is-size-7 has-text-grey">
                <b-icon icon="sync" size="is-small" />
                Last updated: {{ formatTime(lastUpdated) }}
              </span>
            </div>
          </div>
        </div>
        <p class="help has-text-grey mt-2">
          <template v-if="sourceMode === 'single'">
            <strong>Single TX File:</strong> One complete video file containing all segments (soft-segmented using timecodes).
            Workflows: QC, Transcode, TX Ingest, Playout Delivery, VOD Delivery
          </template>
          <template v-else>
            <strong>Separate Segment Files:</strong> Individual source files for each segment that need to be stitched together.
            Workflow: Stitch + QC
          </template>
        </p>
      </div>

      <!-- Workflow Type Selection (Single File Mode) -->
      <div v-if="sourceMode === 'single'" class="workflow-selection mb-4">
        <b-field label="Processing Workflow" grouped>
          <b-select v-model="selectedWorkflowType" expanded>
            <option value="qc_only">QC Analysis Only</option>
            <option value="tx_ingest">TX Ingest (QC + Normalization + MXF Encode)</option>
            <option value="transcode">Transcode (Format Conversion)</option>
            <option value="playout_delivery">Playout Delivery (Copy to Playout Server)</option>
            <option value="vod_delivery">VOD Delivery (Encode for Catchup)</option>
          </b-select>
          <p class="control">
            <b-tooltip :label="getWorkflowDescription()" multilined position="is-left">
              <b-icon icon="information-outline" />
            </b-tooltip>
          </p>
        </b-field>
      </div>

      <!-- Status Summary Bar -->
      <div v-if="statusSummary" class="status-summary mb-4">
        <div class="notification is-light" :class="getStatusNotificationClass()">
          <div class="level is-mobile">
            <div class="level-left">
              <div class="level-item">
                <span class="icon">
                  <i :class="getStatusIcon()"></i>
                </span>
              </div>
              <div class="level-item">
                <div>
                  <p class="has-text-weight-semibold">{{ statusSummary.title }}</p>
                  <p class="is-size-7">{{ statusSummary.detail }}</p>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item" v-if="statusSummary.action">
                <b-button
                  :type="statusSummary.actionType || 'is-primary'"
                  size="is-small"
                  :icon-left="statusSummary.actionIcon"
                  @click="handleStatusAction"
                >
                  {{ statusSummary.action }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SegmentSourceLinker Component -->
      <SegmentSourceLinker
        ref="segmentSourceLinker"
        :episode-id="episodeId"
        :show-title-prop="showTitle"
        :season-number-prop="seasonNumber"
        :hide-type-toggle="true"
        :source-type-prop="sourceMode"
        @job-submitted="onJobSubmitted"
        @files-linked="onFilesLinked"
        @qc-status-changed="onQcStatusChanged"
        @source-type-changed="onSourceTypeChanged"
      />

      <!-- Quick Actions Footer -->
      <div class="quick-actions-footer mt-4 pt-4" style="border-top: 1px solid #dbdbdb;">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <b-button
                type="is-light"
                icon-left="refresh"
                size="is-small"
                @click="refreshData"
                :loading="refreshing"
              >
                Refresh
              </b-button>
            </div>
            <div class="level-item" v-if="hasBulkProcessing">
              <b-button
                type="is-light"
                icon-left="link"
                size="is-small"
                @click="goToBulkProcessing"
              >
                View in Bulk Processing
              </b-button>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <span class="is-size-7 has-text-grey">
                Episode {{ episodeNumber }} of {{ totalEpisodes || '?' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-collapse>
</template>

<script>
import SegmentSourceLinker from '@/components/SegmentSourceLinker.vue'
import { format } from 'date-fns'

export default {
  name: 'EpisodeProcessingPanel',

  components: {
    SegmentSourceLinker
  },

  props: {
    episodeId: {
      type: [Number, String],
      required: true
    },
    showTitle: {
      type: String,
      default: ''
    },
    seasonNumber: {
      type: [Number, String],
      default: null
    },
    episodeNumber: {
      type: [Number, String],
      default: null
    },
    totalEpisodes: {
      type: Number,
      default: null
    },
    mediaId: {
      type: [Number, String],
      default: null
    },
    startOpen: {
      type: Boolean,
      default: true
    },
    // Initial source mode (can be overridden by SegmentSourceLinker's detection)
    initialMode: {
      type: String,
      default: null,
      validator: v => v === null || ['single', 'multi'].includes(v)
    }
  },

  data () {
    return {
      isOpen: this.startOpen,
      sourceMode: this.initialMode || 'single',
      selectedWorkflowType: 'qc_only',
      processingStatus: null,
      qcStatus: null,
      lastUpdated: null,
      refreshing: false
    }
  },

  computed: {
    statusSummary () {
      if (!this.qcStatus) {
        return {
          title: 'No Processing Status',
          detail: 'Link a source file or segment files to begin processing',
          action: null
        }
      }

      const { stitch, qc } = this.qcStatus

      // Check stitch status first (for multi-segment mode)
      if (this.sourceMode === 'multi') {
        if (stitch?.status === 'processing') {
          return {
            title: 'Stitching in Progress',
            detail: `Processing segments... ${stitch.progress || 0}%`,
            action: null
          }
        }
        if (stitch?.status === 'failed') {
          return {
            title: 'Stitch Failed',
            detail: stitch.errorMessage || 'Unknown error during stitching',
            action: 'Re-submit',
            actionType: 'is-warning',
            actionIcon: 'refresh'
          }
        }
      }

      // Check QC status
      if (qc?.status === 'passed' || qc?.status === 'overridden') {
        return {
          title: 'QC Passed',
          detail: `Ready for ${this.selectedWorkflowType === 'playout_delivery' ? 'playout delivery' : 'next workflow step'}`,
          action: this.selectedWorkflowType === 'playout_delivery' ? 'Approve Playout' : null,
          actionType: 'is-success',
          actionIcon: 'check'
        }
      }
      if (qc?.status === 'failed') {
        return {
          title: 'QC Failed',
          detail: `${qc.errorCount || 0} errors, ${qc.warningCount || 0} warnings`,
          action: 'View Report',
          actionType: 'is-danger',
          actionIcon: 'file-document'
        }
      }
      if (qc?.status === 'processing' || qc?.status === 'pending') {
        return {
          title: 'QC In Progress',
          detail: 'Analyzing file quality...',
          action: null
        }
      }

      return {
        title: 'Ready to Process',
        detail: 'Source file linked, ready to submit for processing',
        action: 'Submit Job',
        actionType: 'is-primary',
        actionIcon: 'play'
      }
    },

    hasBulkProcessing () {
      return this.mediaId !== null
    }
  },

  watch: {
    sourceMode (newVal) {
      // Sync mode change to SegmentSourceLinker
      if (this.$refs.segmentSourceLinker) {
        this.$refs.segmentSourceLinker.episodeSourceType = newVal
      }
      this.$emit('mode-changed', newVal)
    }
  },

  methods: {
    onOpen () {
      this.refreshData()
    },

    async refreshData () {
      this.refreshing = true
      try {
        if (this.$refs.segmentSourceLinker) {
          await this.$refs.segmentSourceLinker.loadData()
        }
        this.lastUpdated = new Date()
      } finally {
        this.refreshing = false
      }
    },

    onJobSubmitted (jobData) {
      this.processingStatus = 'Processing'
      this.lastUpdated = new Date()
      this.$emit('job-submitted', jobData)
    },

    onFilesLinked (linkData) {
      this.lastUpdated = new Date()
      this.$emit('files-linked', linkData)
    },

    onQcStatusChanged (status) {
      this.qcStatus = status
      this.updateProcessingStatus()
      this.$emit('qc-status-changed', status)
    },

    onSourceTypeChanged (type) {
      this.sourceMode = type
    },

    updateProcessingStatus () {
      if (!this.qcStatus) {
        this.processingStatus = null
        return
      }

      const { stitch, qc } = this.qcStatus

      if (stitch?.status === 'processing' || qc?.status === 'processing') {
        this.processingStatus = 'Processing'
      } else if (qc?.status === 'passed' || qc?.status === 'overridden') {
        this.processingStatus = 'QC Passed'
      } else if (qc?.status === 'failed') {
        this.processingStatus = 'QC Failed'
      } else if (stitch?.status === 'failed') {
        this.processingStatus = 'Stitch Failed'
      } else if (stitch?.status === 'completed') {
        this.processingStatus = 'Stitched'
      } else {
        this.processingStatus = null
      }
    },

    getStatusTagType () {
      switch (this.processingStatus) {
        case 'QC Passed': return 'is-success'
        case 'QC Failed': return 'is-danger'
        case 'Processing': return 'is-info'
        case 'Stitch Failed': return 'is-danger'
        case 'Stitched': return 'is-success'
        default: return 'is-light'
      }
    },

    getStatusNotificationClass () {
      if (!this.qcStatus) return ''

      const { qc, stitch } = this.qcStatus
      if (qc?.status === 'passed' || qc?.status === 'overridden') return 'is-success'
      if (qc?.status === 'failed' || stitch?.status === 'failed') return 'is-danger'
      if (qc?.status === 'processing' || stitch?.status === 'processing') return 'is-info'
      return ''
    },

    getStatusIcon () {
      if (!this.qcStatus) return 'mdi mdi-help-circle'

      const { qc, stitch } = this.qcStatus
      if (qc?.status === 'passed' || qc?.status === 'overridden') return 'mdi mdi-check-circle'
      if (qc?.status === 'failed' || stitch?.status === 'failed') return 'mdi mdi-alert-circle'
      if (qc?.status === 'processing' || stitch?.status === 'processing') return 'mdi mdi-loading mdi-spin'
      return 'mdi mdi-information'
    },

    getWorkflowDescription () {
      const descriptions = {
        qc_only: 'Run quality control analysis without encoding. Checks loudness, duration, black frames, etc.',
        tx_ingest: 'Full TX workflow: QC analysis, audio normalization (EBU R128), and MXF encoding for playout.',
        transcode: 'Convert to different format or resolution. Select encoding preset for output specifications.',
        playout_delivery: 'Copy processed file to playout server location. Requires QC pass.',
        vod_delivery: 'Encode to broadcaster VOD specification (XDCAM 422 50i) and deliver to catchup platform.'
      }
      return descriptions[this.selectedWorkflowType] || 'Select a workflow type'
    },

    handleStatusAction () {
      if (!this.statusSummary?.action) return

      switch (this.statusSummary.action) {
        case 'Submit Job':
          this.submitProcessingJob()
          break
        case 'Re-submit':
          this.resubmitJob()
          break
        case 'View Report':
          this.viewQcReport()
          break
        case 'Approve Playout':
          this.approveForPlayout()
          break
      }
    },

    submitProcessingJob () {
      if (this.$refs.segmentSourceLinker) {
        if (this.sourceMode === 'multi') {
          this.$refs.segmentSourceLinker.submitStitchJob()
        } else {
          this.$refs.segmentSourceLinker.submitSingleFileForEncodeQc()
        }
      }
    },

    resubmitJob () {
      // Trigger re-submission through SegmentSourceLinker
      if (this.$refs.segmentSourceLinker) {
        if (this.sourceMode === 'multi') {
          this.$refs.segmentSourceLinker.submitStitchJob()
        } else {
          this.$refs.segmentSourceLinker.submitSingleFileForEncodeQc()
        }
      }
    },

    viewQcReport () {
      if (this.$refs.segmentSourceLinker) {
        this.$refs.segmentSourceLinker.openQcReport()
      }
    },

    approveForPlayout () {
      this.$emit('approve-playout', { episodeId: this.episodeId })
    },

    goToBulkProcessing () {
      if (this.mediaId) {
        this.$router.push({
          name: 'ManageEpisodes',
          params: { id: this.mediaId },
          query: { tab: 'processing' }
        })
      }
    },

    formatTime (date) {
      if (!date) return ''
      return format(date, 'HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.card-header {
  cursor: pointer;
}

.card-header:hover {
  background-color: #f5f5f5;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mode-selection-header {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.workflow-selection {
  background-color: #f0f8ff;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid #d4e9ff;
}

.status-summary .notification {
  margin-bottom: 0;
}

.quick-actions-footer {
  background-color: #fafafa;
  margin: 0 -1.25rem -1.25rem;
  padding: 1rem 1.25rem;
}
</style>
